import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { CheckCircle2, Circle, ListTodo, Plus, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuSeparator, DropdownMenuSub,
  DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { smartTasksService, tasksQueryKey, suggestionsQueryKey } from "@/services/smartTasksService";
import { sortTasks } from "@/lib/smartTasksPure";

/**
 * Pinned contextual task panel (Iceberg tip level only): count, next few
 * relevant tasks, completion control, one add action, path to /tasks, and the
 * explicit "Suggest tasks from this thread" AI extraction (approval-gated).
 * Everything deeper lives in the Smart Tasks workspace.
 */
export function TaskMiniPanel({
  threadId,
  threadTitle,
  getThreadText,
  asSubmenu = false,
}: {
  threadId: string;
  threadTitle: string;
  /** caller supplies RLS-visible thread text (minimized before AI) */
  getThreadText: () => string;
  /** DS EDIT 5aug26 — render as a DropdownMenuSub inside the thread ⋮ menu instead of a standalone header icon. */
  asSubmenu?: boolean;
}) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [quickTitle, setQuickTitle] = useState("");
  const [extracting, setExtracting] = useState(false);

  const tasksQuery = useQuery({
    queryKey: tasksQueryKey(user?.id),
    queryFn: () => smartTasksService.listTasks(),
    enabled: !!user,
    staleTime: 30000,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: tasksQueryKey(user?.id) });
    void queryClient.invalidateQueries({ queryKey: suggestionsQueryKey(user?.id) });
  };

  const relevant = useMemo(() => {
    const all = (tasksQuery.data ?? []).filter((t) => t.status === "open" || t.status === "in_progress");
    return sortTasks(all, Date.now());
  }, [tasksQuery.data]);
  const next = relevant.slice(0, 3);

  const addMutation = useMutation({
    mutationFn: () =>
      smartTasksService.createTask({
        title: quickTitle,
        source: { sourceType: "thread", sourceId: threadId, preview: threadTitle.slice(0, 120) },
      }),
    onSuccess: () => { setQuickTitle(""); invalidate(); },
    onError: (e: Error) => toast({ title: "We couldn't save that on our end. Try again?", description: e.message, variant: "destructive" }),
  });

  const completeMutation = useMutation({
    mutationFn: (id: string) => smartTasksService.updateTask(id, { status: "done" }),
    onSuccess: invalidate,
  });

  const suggest = async () => {
    setExtracting(true);
    try {
      const out = await smartTasksService.extractFromContext({
        contextType: "thread",
        contextId: threadId,
        contextLabel: `thread "${threadTitle}"`,
        contextText: getThreadText(),
      });
      toast({
        title: out.length
          ? `${out.length} suggestion${out.length === 1 ? "" : "s"} ready to review`
          : "No clear tasks found in this thread",
        description: out.length ? "Review them in Smart Tasks → Suggested. Nothing becomes a task until you approve it." : undefined,
      });
      invalidate();
    } catch (e) {
      toast({ title: "Couldn't suggest tasks", description: (e as Error).message, variant: "destructive" });
    } finally {
      setExtracting(false);
    }
  };

  if (!user) return null;

  const panelBody = (
    <>
        <div className="text-xs text-muted-foreground px-1 pb-1.5">
          {relevant.length === 0 ? "All clear right now." : `${relevant.length} open task${relevant.length === 1 ? "" : "s"}`}
        </div>

        {next.map((t) => (
          <div key={t.id} className="flex items-center gap-2 px-1 py-1">
            <button
              aria-label="Complete task"
              onClick={(e) => { e.preventDefault(); completeMutation.mutate(t.id); }}
              className="shrink-0"
            >
              {completeMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Circle className="h-4 w-4 text-muted-foreground hover:text-emerald-600" />}
            </button>
            <span className="truncate text-sm flex-1">{t.title}</span>
            {t.dueAt && Date.parse(t.dueAt) < Date.now() && <Badge variant="destructive" className="text-[9px]">due</Badge>}
          </div>
        ))}
        {relevant.length === 0 && (
          <div className="flex items-center gap-2 px-1 py-1 text-sm text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Nothing waiting on you here.
          </div>
        )}

        <DropdownMenuSeparator />
        <div className="flex gap-1.5 px-1 py-1.5" onKeyDown={(e) => e.stopPropagation()}>
          <Input
            value={quickTitle}
            onChange={(e) => setQuickTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && quickTitle.trim()) addMutation.mutate(); }}
            placeholder="Add a task from this thread…"
            className="h-8 text-sm"
          />
          <Button size="icon" className="h-8 w-8 shrink-0" aria-label="Add task"
            onClick={() => addMutation.mutate()} disabled={addMutation.isPending || !quickTitle.trim()}>
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <Button variant="ghost" size="sm" className="w-full justify-start" onClick={() => void suggest()} disabled={extracting}>
          {extracting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
          Suggest tasks from this thread
        </Button>
        <DropdownMenuSeparator />
        <Button asChild variant="ghost" size="sm" className="w-full justify-start">
          <Link href="/tasks"><ListTodo className="h-4 w-4 mr-2" />Open Smart Tasks</Link>
        </Button>
    </>
  );

  // DS EDIT 5aug26 — embedded in the thread ⋮ menu (mobile declutter): same panel, one level deep.
  if (asSubmenu) {
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger data-testid="button-task-mini-panel">
          <ListTodo className="h-4 w-4 mr-2" />
          Tasks{relevant.length > 0 ? ` (${relevant.length > 9 ? "9+" : relevant.length})` : ""}
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="w-72 p-2">{panelBody}</DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="relative flex-shrink-0 h-9 w-9 sm:h-8 sm:w-8 after:absolute after:-inset-1 after:content-[''] sm:after:hidden"
              aria-label={`Tasks (${relevant.length} open)`}
              data-testid="button-task-mini-panel"
            >
              <ListTodo className="h-4 w-4" />
              {relevant.length > 0 && (
                <Badge variant="secondary" className="absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[9px] leading-none flex items-center justify-center">
                  {relevant.length > 9 ? "9+" : relevant.length}
                </Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>Tasks</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" className="w-72 p-2">{panelBody}</DropdownMenuContent>
    </DropdownMenu>
  );
}
